import SupervisorList from '@/components/Supervisor/SupervisorList';
import { Alert, Box, Button, Container, Stack, Typography } from '@mui/material';
import { HandHeart, Info, UserPlus } from '@phosphor-icons/react';
import Greeting from '@/components/calendar/daily/Greeting';
import DisplayDate from '@/components/calendar/daily/DisplayDate';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';
import useRole from '@/personas/useRole';

/**
 * SelectClientPage — handledarens första skärm: vem ska jag stötta?
 *
 * Vyn har två lägen, och båda ska sluta i en handling:
 *  - MED brukare: frågan högst upp, brukarkorten under, och vägen att koppla fler
 *    sist. Det enda som är stort på sidan är korten — det är dem man kom hit för.
 *  - UTAN brukare: en förklaring av vad skärmen är till för, en knapp för att koppla
 *    någon, och en mjuk utväg tillbaka till den egna dagen (setRole('user') rensar
 *    handledarläget, så det blir ingen återvändsgränd).
 *
 * En väntande förfrågan visas som en notis med en knapp, inte som en textlänk bland
 * annan text: det är något som kräver ett svar och ska gå att trycka på direkt.
 */

const SelectClientPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clients, supervisorRequests } = useContext(SupervisorContext);
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);
  const { setRole } = useRole();

  const goToManageClients = () => navigate('/settings');
  // Mjuk utväg: byt roll till brukare (driver setSupervising(false) → rensar
  // currentClient + 'storage'-event, så temat räknas om — ingen återvändsgränd).
  const switchToOwnDay = () => setRole('user');

  const handleClientSelected = () => navigate('/supervisor');

  const header = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      sx={{ pt: 1, pb: 2 }}
    >
      <Greeting />
      <DisplayDate />
    </Stack>
  );

  const requestNotice =
    supervisorRequests.length > 0 ? (
      <Alert
        severity="info"
        variant="outlined"
        icon={<Info size={20} />}
        sx={{ borderRadius: 2, alignItems: 'center' }}
        action={
          <Button
            color="info"
            size="small"
            onClick={goToManageClients}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {t('Answer')}
          </Button>
        }
      >
        {t('You have a request waiting for your answer')}
      </Alert>
    ) : null;

  // Tom-state (S1): handledare utan kopplade brukare. Två utvägar, ingen
  // återvändsgränd — löser edge case 1 (ny handledare) och 7 (stale roll).
  if (clients.length === 0) {
    return (
      <Container sx={{ pb: 4 }}>
        {header}
        <Stack spacing={2.5} alignItems="center" textAlign="center" px={2} pt={3}>
          <Box
            aria-hidden
            sx={{
              width: 104,
              height: 104,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: tk.secondaryBg,
              color: tk.primary,
            }}
          >
            <HandHeart size={48} weight="duotone" />
          </Box>
          <Typography variant="h6" component="h1" fontWeight={700} sx={{ color: tk.text }}>
            {t('This is where you gather the people you support')}
          </Typography>
          <Typography variant="body1" sx={{ color: tk.textSoft, maxWidth: 320 }}>
            {t('Connect a person so you can plan the day together.')}
          </Typography>

          <Button
            variant="contained"
            size="large"
            fullWidth
            startIcon={<UserPlus size={20} />}
            onClick={goToManageClients}
            sx={{ maxWidth: 360, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            {t('Connect a user')}
          </Button>

          {requestNotice}

          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ color: tk.textSoft }}>
              {t('Do you want to use the app for your own day instead?')}
            </Typography>
            <Button
              variant="text"
              onClick={switchToOwnDay}
              sx={{ textTransform: 'none', fontWeight: 700, color: tk.primary }}
            >
              {t('Switch to My own day')}
            </Button>
          </Box>
        </Stack>
      </Container>
    );
  }

  return (
    <Container sx={{ pb: 4 }}>
      {header}
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6" component="h1" fontWeight={700} sx={{ color: tk.text }}>
            {t('Who do you want to support today?')}
          </Typography>
          <Typography variant="body2" sx={{ color: tk.textSoft, mt: 0.5 }}>
            {t('Everything you do next applies to the person you pick here.')}
          </Typography>
        </Box>

        {requestNotice}

        {/* Rubriken i listan är av: frågan ovanför säger redan vad korten är. */}
        <SupervisorList onSelect={handleClientSelected} showHeading={false} />

        <Button
          variant="outlined"
          startIcon={<UserPlus size={18} />}
          onClick={goToManageClients}
          sx={{
            alignSelf: 'flex-start',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            borderColor: tk.border,
            color: tk.text,
          }}
        >
          {t('Connect a user')}
        </Button>

        <Typography variant="caption" sx={{ color: tk.textSoft }}>
          {t('You can switch role in settings')}
        </Typography>
      </Stack>
    </Container>
  );
};

export default SelectClientPage;
